import { Box, Chip } from "@mui/material";
import { Add, Assessment, Delete, Edit } from "@mui/icons-material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ADD_WEEKLY_WORKER_PAYMENT,
  DELETE_WEEKLY_WORKER_PAYMENT,
  EDIT_WEEKLY_WORKER_PAYMENT,
  VIEW_WEEKLY_WORKER_PAYMENT_REPORT,
} from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, DeleteModal, MTable, PageHeader, TableActions } from "../../components/v3";
import { HumanResourceService, ProjectService } from "../../services";
import { useHasPermission } from "../../utils";
import { WEEKLY_WORKER_PAYMENT_ROUTES } from "./constants";

type PaymentStatus = "UNPAID" | "PARTIALLY_PAID" | "PAID";

const PAYMENT_STATUS_CHIP: Record<PaymentStatus, { label: string; color: "default" | "warning" | "success" }> = {
  UNPAID: { label: "Unpaid", color: "default" },
  PARTIALLY_PAID: { label: "Partially Paid", color: "warning" },
  PAID: { label: "Paid", color: "success" },
};

export const WeeklyPaymentHistory = () => {
  const navigate = useNavigate();
  const canAdd = useHasPermission(ADD_WEEKLY_WORKER_PAYMENT);
  const canEdit = useHasPermission(EDIT_WEEKLY_WORKER_PAYMENT);
  const canDelete = useHasPermission(DELETE_WEEKLY_WORKER_PAYMENT);
  const canViewReport = useHasPermission(VIEW_WEEKLY_WORKER_PAYMENT_REPORT);
  const [sessions, setSessions] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [stageNames, setStageNames] = useState<Record<number, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data } = await HumanResourceService.getWeeklyWorkPaymentSessions({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      });
      setSessions(data?.rows || []);
      setRowCount(data?.count || 0);
    } catch (error) {
      console.error("Error fetching weekly payment history:", error);
      toast.error("Failed to fetch weekly payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    const stageIds = [...new Set(sessions.map((s) => s.projectStage?.stageId).filter(Boolean))];
    if (!stageIds.length) return;
    ProjectService.getStages({ ids: stageIds.join(","), limit: stageIds.length })
      .then(({ data }: any) => {
        const map: Record<number, string> = {};
        (data?.rows || []).forEach((stage: any) => {
          map[stage.id] = stage.name;
        });
        setStageNames((prev) => ({ ...prev, ...map }));
      })
      .catch((error) => console.error("Error fetching stage names:", error));
  }, [sessions]);

  const handleEdit = (sessionId: string) => {
    navigate(WEEKLY_WORKER_PAYMENT_ROUTES.session.replace(":sessionId", sessionId));
  };

  const handleDeleteClick = (sessionId: string) => {
    setDeleteTarget(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await HumanResourceService.deleteWeeklyWorkPaymentSession(deleteTarget);
      toast.success("Weekly payment session deleted successfully");
      setDeleteTarget(null);
      fetchSessions();
    } catch (error: any) {
      const response = error?.response?.data;
      const message = Array.isArray(response?.message)
        ? response.message.join(", ")
        : response?.message || "Failed to delete weekly payment session";
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const columnHeaders = [
    {
      field: "dateRange",
      headerName: "Date Range",
      flex: 1.3,
      sortable: false,
      renderCell: (params: any) => {
        const { startDate, endDate } = params.row;
        if (!startDate || !endDate) return "—";
        return `${moment(startDate).format("DD MMM YYYY")} - ${moment(endDate).format("DD MMM YYYY")}`;
      },
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (params.value ? moment(params.value).format("DD MMM YYYY") : "—"),
    },
    { field: "workersCount", headerName: "Workers", flex: 0.7, sortable: false },
    {
      field: "project",
      headerName: "Project",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => params.row.project?.name || "—",
    },
    {
      field: "stage",
      headerName: "Stage",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const stageId = params.row.projectStage?.stageId;
        return (stageId && stageNames[stageId]) || "—";
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.3,
      sortable: false,
      renderCell: (params: any) => params.value || "—",
    },
    {
      field: "paymentStatus",
      headerName: "Status",
      flex: 0.8,
      sortable: false,
      renderCell: (params: any) => {
        const chip = PAYMENT_STATUS_CHIP[params.value as PaymentStatus] || PAYMENT_STATUS_CHIP.UNPAID;
        return <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const paymentStatus: PaymentStatus = params.row.paymentStatus || "UNPAID";
        const actions = [];

        if (canEdit) {
          actions.push({ icon: Edit, type: "edit", label: "Edit", onClick: () => handleEdit(params.row.sessionId) });
        }
        if (canViewReport) {
          actions.push({
            icon: Assessment,
            type: "pay",
            label: "View Report",
            onClick: () => navigate(WEEKLY_WORKER_PAYMENT_ROUTES.report.replace(":sessionId", params.row.sessionId)),
          });
        }
        if (paymentStatus === "UNPAID" && canDelete) {
          actions.push({ icon: Delete, type: "delete", label: "Delete", onClick: () => handleDeleteClick(params.row.sessionId) });
        }

        return <TableActions item={params} actions={actions} />;
      },
    },
  ];

  const rows = sessions.map((session) => ({ ...session, id: session.sessionId }));

  return (
    <>
      <PageLayout>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader
            title="Weekly Payment History"
            buttons={
              canAdd
                ? [
                    {
                      buttonText: "Add",
                      buttonIcon: Add,
                      onClick: () => navigate(WEEKLY_WORKER_PAYMENT_ROUTES.root),
                    },
                  ]
                : []
            }
          />
        </Box>

        <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
          <CardLayout>
            <MTable
              columnHeaders={columnHeaders}
              tableRows={rows}
              rowCount={rowCount}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationChange={setPaginationModel}
            />
          </CardLayout>
        </Box>
      </PageLayout>

      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this weekly payment session? This cannot be undone."
      />
    </>
  );
};
